import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getReports,
  getReportById,
  createPersonalReport,
  previewPersonalReport,
  getEstablishmentReports,
  getEstablishmentReportById,
  createEstablishmentReport,
  previewEstablishmentReport,
  getOrderedItems,
  getOrderedItemsByDay,
} from "../api/reports";
import type {
  ReportFilters,
  EstablishmentReportFilters,
  CreateReportRequest,
  CreateEstablishmentReportRequest,
  OrderedItemsFilters,
  OrderedItemsByDayFilters,
} from "../api/reports";
import { getCancellations } from "../api/reportsCancellations";
import type { CancellationsFilters } from "../api/reportsCancellations";
import {
  getBillsHistory,
  getBillHistoryById,
} from "../api/reportsBillsHistory";
import type { BillHistoryFilters } from "../api/reportsBillsHistory";
import { getBillsSplits } from "../api/reportsBillsSplits";
import type { BillSplitsFilters } from "../api/reportsBillsSplits";
import { getDiscountsReport } from "../api/reportsDiscounts";
import { getPriceChangesReport } from "../api/reportsPriceChanges";
import {
  getKitchenMonitorReport,
  type KitchenMonitorReportFilters,
} from "../api/reportsKitchenMonitors";
import type { PriceChangeReportFilters } from "../api/reportsPriceChanges";
import type { DiscountReportFilters } from "../api/reportsDiscounts";
import { getWaitersReport } from "../api/reportsWaiters";
import type { WaiterReportFilters } from "../api/reportsWaiters";
import { getCardPaymentsReport } from "../api/reportsCardPayments";
import type { CardPaymentsReportFilters } from "../api/reportsCardPayments";
import { getStationsReport } from "../api/reportsStations";
import type { StationReportFilters } from "../api/reportsStations";
import { getSalesByPeriodsReport } from "../api/reportsSalesByPeriods";
import type { SalesByPeriodsFilters } from "../api/reportsSalesByPeriods";
import { getDashboard } from "../api/reportsDashboard";
import type { DashboardFilters } from "../api/reportsDashboard";
import {
  getReservationGuestVisits,
  getReservationsGuestsReport,
  getReservationsJournalReport,
  getReservationsOccupancyReport,
  type ReservationGuestVisitsFilters,
  type ReservationOccupancyFilters,
  type ReservationReportFilters,
} from "../api/reportsReservations";

// Personal reports
export const usePersonalReports = (filters: ReportFilters) => {
  return useQuery({
    queryKey: ["reports", "personal", filters],
    queryFn: () => getReports(filters),
    staleTime: 0,
    retry: false, // Не правим retry - при грешка (вкл. 403) я показваме веднага
  });
};

export const usePersonalReportById = (reportId: number | null) => {
  return useQuery({
    queryKey: ["reports", "personal", "detail", reportId],
    queryFn: () => getReportById(reportId!),
    enabled: reportId !== null,
    staleTime: 0,
    retry: false, // Не правим retry - при грешка (вкл. 403) я показваме веднага
  });
};

export const useGeneratePersonalReport = () => {
  return useMutation({
    mutationFn: (data: CreateReportRequest) => createPersonalReport(data),
  });
};

export const usePreviewPersonalReport = () => {
  return useMutation({
    mutationFn: (data: CreateReportRequest) => previewPersonalReport(data),
  });
};

// Establishment reports
export const useEstablishmentReports = (
  filters: EstablishmentReportFilters,
) => {
  return useQuery({
    queryKey: ["reports", "establishment", filters],
    queryFn: () => getEstablishmentReports(filters),
    staleTime: 0,
    retry: false, // Не правим retry - при грешка (вкл. 403) я показваме веднага
  });
};

export const useEstablishmentReportById = (reportId: number | null) => {
  return useQuery({
    queryKey: ["reports", "establishment", "detail", reportId],
    queryFn: () => getEstablishmentReportById(reportId!),
    enabled: reportId !== null,
    staleTime: 0,
    retry: false, // Не правим retry - при грешка (вкл. 403) я показваме веднага
  });
};

export const useGenerateEstablishmentReport = () => {
  return useMutation({
    mutationFn: (data: CreateEstablishmentReportRequest) =>
      createEstablishmentReport(data),
  });
};

export const usePreviewEstablishmentReport = () => {
  return useMutation({
    mutationFn: (data: CreateEstablishmentReportRequest) =>
      previewEstablishmentReport(data),
  });
};

// Cancellations reports
export const useCancellations = (filters: CancellationsFilters) => {
  return useQuery({
    queryKey: ["reports", "cancellations", filters],
    queryFn: () => getCancellations(filters),
    staleTime: 0,
  });
};

// Bills history reports
export const useBillsHistory = (
  filters: BillHistoryFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["reports", "bills-history", filters],
    queryFn: () => getBillsHistory(filters),
    staleTime: 0,
    // Старите редове остават на екрана, докато новите се зареждат: смяна на
    // подредбата, страницата или периода иначе размонтира цялата таблица в
    // spinner и екранът „примигва" при всеки клик по заглавие на колона.
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
};

// Взима историята на конкретна сметка по ID - използва се от Сторно и
// Отстъпки справките за модала с детайли (затова context=reports — scope-ът
// идва от правото за съответната справка, не от view_all_users_bills)
export const useBillHistoryById = (billId: number | null) => {
  return useQuery({
    queryKey: ["reports", "bills-history", "detail", billId],
    queryFn: () => getBillHistoryById(billId!, "reports"),
    enabled: billId !== null,
    staleTime: 0,
  });
};

// Bills splits reports
export const useBillsSplits = (filters: BillSplitsFilters) => {
  return useQuery({
    queryKey: ["reports", "bills-splits", filters],
    queryFn: () => getBillsSplits(filters),
    staleTime: 0,
  });
};

// Ordered items (assortment)
export const useOrderedItems = (filters: OrderedItemsFilters) => {
  return useQuery({
    queryKey: ["reports", "ordered-items", filters],
    queryFn: () => getOrderedItems(filters),
    staleTime: 0,
    // Страница/сортиране/търсене: докато идва новата страница, остава старата
    // (таблицата показва „опреснява"), а превключвателят „Всички / Без обедно /
    // Само обедно" (по `has_lunch`) не изчезва и не се появява наново. Само в
    // същия период — друг период (и „Сравни") чака своите данни, не показва
    // чуждите.
    placeholderData: (previous, previousQuery) => {
      const previousFilters = previousQuery?.queryKey[2] as OrderedItemsFilters | undefined;
      return previousFilters?.start_date === filters.start_date &&
        previousFilters?.end_date === filters.end_date
        ? previous
        : undefined;
    },
  });
};

// Разбивката по дни зад един ред на „Асортименти" — модалът на „По артикули"
// и на „По категории". Пита се само когато има какво да се пита: без период
// или без кликнат ред заявката е гарантирано празна.
export const useOrderedItemsByDay = (
  filters: OrderedItemsByDayFilters,
  options?: { enabled?: boolean },
) => {
  const hasPeriod = Boolean(filters.start_date && filters.end_date);
  const hasTarget =
    filters.item_id !== undefined ||
    filters.station_id !== undefined ||
    (filters.category_ids?.length ?? 0) > 0;

  return useQuery({
    queryKey: ["reports", "ordered-items", "by-day", filters],
    queryFn: () => getOrderedItemsByDay(filters),
    enabled: (options?.enabled ?? true) && hasPeriod && hasTarget,
    staleTime: 0,
  });
};

// Discounts report
export const useDiscountsReport = (filters: DiscountReportFilters) => {
  return useQuery({
    queryKey: ["reports", "discounts", filters],
    queryFn: () => getDiscountsReport(filters),
    staleTime: 0,
  });
};

// Price changes report - Справка „Променени цени"
export const usePriceChangesReport = (filters: PriceChangeReportFilters) => {
  return useQuery({
    queryKey: ["reports", "price-changes", filters],
    queryFn: () => getPriceChangesReport(filters),
    staleTime: 0,
  });
};

// Kitchen monitors report - „Справка монитори"
export const useKitchenMonitorReport = (
  filters: KitchenMonitorReportFilters,
) => {
  return useQuery({
    queryKey: ["reports", "kitchen-monitors", filters],
    queryFn: () => getKitchenMonitorReport(filters),
    staleTime: 0,
  });
};

// Waiters report - Справка по сервитьори
export const useWaitersReport = (filters: WaiterReportFilters) => {
  return useQuery({
    queryKey: ["reports", "waiters", filters],
    queryFn: () => getWaitersReport(filters),
    staleTime: 0,
  });
};

// Stations report - Справка по станции (обекти)
export const useStationsReport = (filters: StationReportFilters) => {
  return useQuery({
    queryKey: ["reports", "stations", filters],
    queryFn: () => getStationsReport(filters),
    staleTime: 0,
    // Смяна на сервитьор/период държи старите редове на екрана, докато новите
    // се зареждат — иначе таблицата (и чипът „Сервитьори" в нея) изчезва.
    placeholderData: (previous) => previous,
  });
};

// Sales by periods — „Продажби по периоди": оборотът, нарязан по време.
export const useSalesByPeriodsReport = (
  filters: SalesByPeriodsFilters,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["reports", "sales-by-periods", filters],
    queryFn: () => getSalesByPeriodsReport(filters),
    enabled,
    staleTime: 0,
    // Смяна на групиране/филтър държи старите редове на екрана, докато новите
    // се зареждат — таблицата не мига до празно.
    placeholderData: (previous) => previous,
  });
};

// Card payments — „Картови плащания": сверката с терминала и банката.
export const useCardPaymentsReport = (filters: CardPaymentsReportFilters) => {
  return useQuery({
    queryKey: ["reports", "card-payments", filters],
    queryFn: () => getCardPaymentsReport(filters),
    staleTime: 0,
    // Старите редове остават на екрана, докато новите се зареждат: смяна на
    // подредбата, страницата или периода иначе размонтира цялата таблица в
    // spinner и екранът „примигва" при всеки клик по заглавие на колона.
    placeholderData: (previous) => previous,
  });
};

// Dashboard - Обобщена информация за заведението
export const useDashboard = (filters?: DashboardFilters) => {
  return useQuery({
    queryKey: ["reports", "dashboard", filters ?? {}],
    queryFn: () => getDashboard(filters),
    staleTime: 0,
    // Старите редове остават на екрана, докато новите се зареждат: смяна на
    // подредбата, страницата или периода иначе размонтира цялата таблица в
    // spinner и екранът „примигва" при всеки клик по заглавие на колона.
    placeholderData: (previous) => previous,
  });
};

// Reservation reports - трите справки за резервации (по едно право на справка)
export const useReservationsJournalReport = (
  filters: ReservationReportFilters,
) => {
  return useQuery({
    queryKey: ["reports", "reservations-journal", filters],
    queryFn: () => getReservationsJournalReport(filters),
    staleTime: 0,
  });
};

export const useReservationsOccupancyReport = (
  filters: ReservationOccupancyFilters,
) => {
  return useQuery({
    queryKey: ["reports", "reservations-occupancy", filters],
    queryFn: () => getReservationsOccupancyReport(filters),
    staleTime: 0,
  });
};

export const useReservationsGuestsReport = (
  filters: ReservationReportFilters,
) => {
  return useQuery({
    queryKey: ["reports", "reservations-guests", filters],
    queryFn: () => getReservationsGuestsReport(filters),
    staleTime: 0,
  });
};

// Историята на един гост от „Гости" — заявката тръгва чак при отворен модал.
// `enabled` = false пази последните данни (затварящият се модал не примигва).
export const useReservationGuestVisits = (
  filters: ReservationGuestVisitsFilters | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["reports", "reservations-guest-visits", filters],
    queryFn: () => getReservationGuestVisits(filters!),
    enabled: enabled && filters !== null,
    staleTime: 0,
  });
};
